import React from "react";
import { Link } from "react-router-dom";

// El botón de píldora estaba reescrito a mano una docena de veces, con
// tamaños y estados de hover ligeramente distintos en cada página, y ninguna
// de las copias tenía estilo de foco: un usuario de teclado no veía dónde
// estaba.
//
// `gradient-terra`, la clase que usaban todas, es además un nombre engañoso:
// no es ningún degradado, sino el azul plano de acento. Aquí desaparece.
const VARIANTS = {
  // Acción principal sobre fondo claro.
  primary: "bg-primary text-primary-foreground hover:opacity-90 focus-ring",
  // Acción principal sobre fondo oscuro (héroes, secciones a sangre).
  light: "bg-white text-black hover:bg-white/90 focus-ring-light",
  // Secundaria sobre fondo oscuro.
  outlineLight: "border border-white/40 text-white hover:bg-white/10 focus-ring-light",
  // Secundaria sobre fondo claro.
  outline: "border border-border text-foreground hover:bg-muted focus-ring",
  // Dorado, reservado al Evangelio del día.
  gold: "bg-[var(--accent-gold)] text-black hover:opacity-90 focus-ring",
};

const SIZES = {
  sm: "px-4 py-2 text-[13px]",
  md: "px-6 py-3 text-sm",
  lg: "px-9 py-4 text-base",
};

/**
 * Botón/enlace de acción con forma de píldora.
 *
 * Renderiza <Link>, <a> o <button> según reciba `to`, `href` o ninguno.
 * El `:active` con escala mínima es el detalle que da sensación táctil.
 */
export default function CTAButton({
  children,
  to,
  href,
  variant = "primary",
  size = "md",
  className = "",
  type = "button",
  ...rest
}) {
  const classes = [
    "inline-flex items-center justify-center gap-2 font-medium w-fit",
    "transition-[opacity,background-color,transform,border-color]",
    "duration-fast ease-out-apple active:scale-[0.97]",
    "disabled:opacity-50 disabled:pointer-events-none",
    VARIANTS[variant] ?? VARIANTS.primary,
    SIZES[size] ?? SIZES.md,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const style = { borderRadius: "var(--radius-pill)" };

  if (to) {
    return (
      <Link to={to} className={classes} style={style} {...rest}>
        {children}
      </Link>
    );
  }

  if (href) {
    const external = /^https?:\/\//.test(href);
    return (
      <a
        href={href}
        className={classes}
        style={style}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        {...rest}
      >
        {children}
      </a>
    );
  }

  return (
    <button type={type} className={classes} style={style} {...rest}>
      {children}
    </button>
  );
}

/**
 * Enlace de texto con flecha que se separa al pasar el ratón.
 *
 * Se repetía como `hover:gap-3 transition-all` en CarouselSection, Home,
 * MiembroCard y varias páginas. `transition-all` anima además propiedades de
 * layout, así que se acota a `gap`.
 */
export function ArrowLink({ children, to, href, className = "", ...rest }) {
  const classes = `inline-flex items-center gap-2 text-sm font-medium text-primary hover:gap-3 transition-[gap] duration-fast ease-out-apple focus-ring ${className}`;

  if (href) {
    return (
      <a href={href} className={classes} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={classes} {...rest}>
      {children}
    </Link>
  );
}
